import styled from '@emotion/styled';
import React, { FC } from 'react';
import { Markdown } from 'src/modules/newPrograms/components/Markdown';
import { Page, PageProps } from 'src/modules/newPrograms/components/Page';
import { ProgramPageFragment } from 'src/modules/newPrograms/documents/getProgram.generated';
import {
    VideoJsTextTrackKind,
    VideoPlayer,
    VideoPlayerSupportedFileFormats,
} from 'src/modules/programs/components/VideoPlayer';
import { pxToRem } from 'src/shared/helpers/pxToRem';
import { mediaQueries } from 'src/shared/styles/variables';
import * as Sentry from '@sentry/browser';
import { PageContentVerticalAlignment } from 'src/generated/webapp_gql';

const Container = styled.div({
    display: 'flex',
    alignItems: 'center',
    [mediaQueries.md]: {
        flexWrap: 'wrap',
    },
    flexDirection: 'column-reverse',
    justifyContent: 'center',
});

const MarkdownContainer = styled.div({
    maxWidth: pxToRem(700),
    width: pxToRem(700),
});

const VideoContainer = styled.div({
    marginBottom: pxToRem(30),
});

interface VideoPageProps extends PageProps {
    page: ProgramPageFragment;
}

const ErrorContainer = () => {
    return (
        <div>
            The content on this page is misconfigured. We've been alerted and we're working on
            fixing it! In the meantime, you can skip this video and come back to it later once we've
            fixed it!
        </div>
    );
};

interface VideoPageWrapperProps {
    onNavigateNext: VoidFunction;
    onNavigatePrev: VoidFunction;
    contentVerticalAlignment: PageContentVerticalAlignment;
}

const VideoPageWrapper: FC<VideoPageWrapperProps> = ({
    onNavigatePrev,
    onNavigateNext,
    contentVerticalAlignment,
    children,
}) => {
    return (
        <Page
            onNavigatePrev={onNavigatePrev}
            onNavigateNext={onNavigateNext}
            contentVerticalAlignment={contentVerticalAlignment}
            justifyNavigation="center"
        >
            <Container>
                <MarkdownContainer>{children}</MarkdownContainer>
            </Container>
        </Page>
    );
};

export const VideoPage: FC<VideoPageProps> = ({ page, onNavigatePrev, onNavigateNext }) => {
    if (
        !page.videoContent ||
        !page.videoContent.source ||
        !page.videoContent.textTrack ||
        !page.videoContent.poster
    ) {
        Sentry.withScope(scope => {
            scope.setExtras({
                id: page.id,
                poster: page.videoContent?.poster,
                source: page.videoContent?.source,
                textTrack: page.videoContent?.textTrack,
            });
            Sentry.captureException('Program VideoPage is misconfigured');
        });
        // TODO (garet): https://modernhealthhq.slack.com/archives/C01KGNPSDPD/p1629941708003200
        // If we are keeping this content, we need to translate it before launching a real VideoPage.
        return (
            <VideoPageWrapper
                onNavigateNext={onNavigateNext}
                onNavigatePrev={onNavigatePrev}
                contentVerticalAlignment={page.contentVerticalAlignment}
            >
                <ErrorContainer />
            </VideoPageWrapper>
        );
    }
    const source = {
        src: page.videoContent.source,
        type: VideoPlayerSupportedFileFormats.MP4,
    };
    const poster = page.videoContent.poster;
    // For now, assume we have only one textTrack
    // TODO (garet): Figure out how to dynamically populate label and srcLang
    const tracks = [
        {
            kind: VideoJsTextTrackKind.Subtitles,
            label: 'English',
            srcLang: 'en',
            src: page.videoContent.textTrack,
        },
    ];

    return (
        <VideoPageWrapper
            onNavigateNext={onNavigateNext}
            onNavigatePrev={onNavigatePrev}
            contentVerticalAlignment={page.contentVerticalAlignment}
        >
            <VideoContainer>
                <VideoPlayer source={source} poster={poster} tracks={tracks} />
            </VideoContainer>
            <Markdown
                textHorizontalAlignment={page.contentHorizontalAlignment}
                text={page.content ?? ''}
            />
        </VideoPageWrapper>
    );
};
